import { WindowType } from './windowManager';
import NonTransactionalIntent from '../constants/nonTransactionalIntents';
import TransactionIntent from '../constants/transactionIntents';
import { store } from './userStore';
import { BrokerPlatformActions } from '../constants/transactionStatus';
import errorMap from './errorMap';
import { scErrorLogger } from '../services/sentryService';

type IntentURLParams = {
  broker: string;
  openMode: WindowType;
  gateway: string;
  action: BrokerPlatformActions;
  // optional, only used for gatewayLogin, using this on iframe brokers breaks the login
  gatewayName?: string;
  transactionId?: string;
};

const formIntentUrl = (path: string, params: IntentURLParams) => {
  const pathString = new URLSearchParams({
    v: VERSION,
    ...params,
  });
  return `${path}?${pathString}`;
};

const formLoginIntentUrl = (path: string, params: IntentURLParams) => {
  // params and path are passed as params so that we can navigate to them after login
  const paramStr = new URLSearchParams({
    v: VERSION,
    ...params,
    ...(ENV === 'staging' && { staging: 'true' }),
  });
  return `${new URLSearchParams({
    params: paramStr.toString(),
    path,
  })}`;
};

/** broker platform paths */
const pathMap = {
  seeOrders: '/orders',
  brokerLogout: '/brokerLogout',
  checkSession: '/checkSession',
  transaction: '/gatewaytransaction/',
};

/**
 * Intents for which we want to open a specific path
 * Transactional intents always use the default path
 * */
const intentToPathMap = {
  [NonTransactionalIntent.SEE_ORDERS]: pathMap.seeOrders,
  [NonTransactionalIntent.BROKER_LOGOUT]: pathMap.brokerLogout,
};

type IntentUrlConfig = Omit<IntentURLParams, 'action'> & {
  /** we make action optional here, this function will default to 'gatewaytransaction' if not provided */
  action?: BrokerPlatformActions;
  /** true for iframe brokers like hdfc and axis */
  isLoginIntentURL: boolean;
  // intent may not be present for login() method
  intent?: NonTransactionalIntent | TransactionIntent;
  // only present for transactional intents
  transactionId?: string;
};
function getIntentURL(config: IntentUrlConfig): string {
  const {
    broker,
    openMode,
    gateway,
    action = BrokerPlatformActions.TRANSACTION,
    gatewayName,
  } = config;
  const commonParams = {
    action,
    broker,
    openMode,
    gateway,
    gatewayName,
  };
  if (action === BrokerPlatformActions.GATEWAY_LOGIN) {
    return formIntentUrl(pathMap.checkSession, {
      ...commonParams,
      ...(ENV === 'staging' && { staging: 'true' }),
    });
  }
  // see if we want to open a specific path for the intent, if not use the default path
  const path =
    intentToPathMap[config.intent] ||
    `${pathMap.transaction}${config.transactionId}`;
  const urlParams: IntentURLParams = {
    ...commonParams,
    // add transactionId if its a transactional intent (see intentToPathMap)
    ...(!intentToPathMap[config.intent] && {
      transactionId: config.transactionId,
    }),
  };
  if (config.isLoginIntentURL) {
    return formLoginIntentUrl(path, urlParams);
  }
  return formIntentUrl(path, urlParams);
}

export type BrokerConfig = {
  getIntentURL: (urlParams: any) => string;
  transactionOpenMode: WindowType;
};

type UnsupportedConfig = {
  transactionOpenMode: WindowType;
};
/**
 * flags not present in the brokerConfig.json
 * we will need to update this for broker who we cant open the transaction in iframe
 */
const unsupportedBrokerConfigs: Record<string, UnsupportedConfig> = {
  axis: {
    transactionOpenMode: WindowType.NEWTAB,
  },

  aliceblue: {
    transactionOpenMode: WindowType.NEWTAB,
  },

  hdfc: {
    transactionOpenMode: WindowType.NEWTAB,
  },

  kotak: {
    transactionOpenMode: WindowType.NEWTAB,
  },

  icici: {
    transactionOpenMode: WindowType.NEWTAB,
  },
};

const brokerConfigUrl = `https://config.smallcase.com/brokerconfig/${ENV}/brokerConfig.json`;

type RemoteBrokerConfig = {
  broker: string;
  brokerDisplayName: string;
  brokerShortName: string;
  platformURL: string;
  preLoginURL: string;
  leprechaunURL: string;
  baseLoginURL: string;
  deepLink: string;
  accountOpeningURL: string;
  isRedirectURL: boolean;
  trustedBroker: boolean;
  isIframePlatform: boolean;
  visible: boolean;
  topBroker: boolean;
  popularity: number;
  gatewayVisible: boolean;
  commsUrl: string;
};

const brokerConfigStore = {
  data: null,
};
export const initBrokerConfig = async () => {
  try {
    const response = await fetch(brokerConfigUrl);
    const brokerConfigs: RemoteBrokerConfig[] = await response.json();
    brokerConfigStore.data = brokerConfigs
      .filter((brokerConfig) => brokerConfig.gatewayVisible)
      .reduce((acc, brokerConfig) => {
        acc[brokerConfig.broker] = brokerConfig;
        return acc;
      }, {});
  } catch (error) {
    scErrorLogger.log(new Error('Failed to load broker config'));
    throw errorMap.API_ERROR;
  }
};

const LEPRECHAUN_SUFFIX = '-leprechaun';

export function fetchBrokerConfig(broker: string) {
  if (!brokerConfigStore.data) {
    throw new Error('Broker config not initialized');
  }
  // Broker name is lowercase-d and leprechaun suffix is removed
  const brokerNormalized = broker.toLowerCase().replace(LEPRECHAUN_SUFFIX, '');
  const isLeprechaun = broker.toLowerCase().indexOf(LEPRECHAUN_SUFFIX) > -1;

  const { platformURL, leprechaunURL, isIframePlatform, baseLoginURL } =
    brokerConfigStore.data[brokerNormalized] as RemoteBrokerConfig;

  // if third party cookie is not supported, we open the transaction in a new tab
  // otherwise we check if the broker restricts transactions on open mode
  // else we default to iframe
  const transactionOpenMode = !store.thirdPartyCookieSupported
    ? WindowType.NEWTAB
    : unsupportedBrokerConfigs[brokerNormalized]?.transactionOpenMode ||
      WindowType.IFRAME;

  // Use baseLoginURL for iframe brokers
  let platformUrlToUse = isIframePlatform ? baseLoginURL : platformURL;
  // always use leprechaun url for leprechaun transactions
  if (isLeprechaun) {
    platformUrlToUse = leprechaunURL;
  }

  return {
    transactionOpenMode,
    getIntentURL: (urlParams: Omit<IntentUrlConfig, 'isLoginIntentURL'>) =>
      `${platformUrlToUse}${getIntentURL({
        ...urlParams,
        isLoginIntentURL: isIframePlatform,
      })}`,
    skipLogin: isIframePlatform,
  };
}

export const filterValidBrokers = (
  brokers: string[],
  allowedBrokers: string[],
) => {
  // returns the brokers that are available in the brokerMap
  const allowedBrokersThatExists = allowedBrokers.filter((b) =>
    Object.keys(brokerConfigStore.data).includes(b),
  );
  if (!brokers) {
    return allowedBrokersThatExists;
  }
  if (Array.isArray(brokers) && brokers.length === 0) {
    return allowedBrokersThatExists;
  }
  return brokers.filter(
    (broker) =>
      allowedBrokersThatExists.indexOf(broker.split('-leprechaun')[0]) > -1,
  );
};
